import React, { useMemo } from 'react';
import TextField from '@mui/material/TextField';
import moment from 'moment';
import {
  API_DATE_FORMAT,
  TIMESTAMP_DATE_TIME_FORMAT,
  US_DATE_FORMAT,
} from 'utils/appConstants';
import { AttributeDataTypes } from 'components/DataIngestion/constants';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';


export const ParametersField = ({
  name,
  constraints,
  dataType,
  setErrors,
  runDetails,
  updateRunDetails,
  description = '',
  exampleValue = '',
}) => {
  const fieldType = useMemo(() => {
    if (dataType === AttributeDataTypes.TIMESTAMP) {
      return TIMESTAMP_DATE_TIME_FORMAT;
    }
    if (dataType === AttributeDataTypes.DATE) {
      return US_DATE_FORMAT;
    }
    return dataType;
  }, [dataType]);

  const handlePickerChange = (e) => {
    let formattedValue = e || '';
    if (dataType === AttributeDataTypes.DATE) {
      formattedValue = moment(formattedValue).format(API_DATE_FORMAT);
    }
    else if (dataType === AttributeDataTypes.TIMESTAMP) {
      formattedValue = moment(formattedValue).format('YYYY-MM-DDTHH:mm:ss');
    }

    // For DATE fields, normalize to start of day for accurate comparison
    // For TIMESTAMP fields, preserve the full timestamp
    const parseConstraintDate = (date) => {
      if (!date) return null;
      const parsedDate = moment.utc(date);
      return dataType === AttributeDataTypes.DATE ? parsedDate.startOf('day') : parsedDate;
    };

    const min = parseConstraintDate(constraints.minDate);
    const max = parseConstraintDate(constraints.maxDate);

    const selected = dataType === AttributeDataTypes.DATE
      ? moment.utc(e).startOf('day')
      : moment.utc(e);

    const formattedMin = min ? min.format(dataType === AttributeDataTypes.DATE ? US_DATE_FORMAT : TIMESTAMP_DATE_TIME_FORMAT) : '';
    const formattedMax = max ? max.format(dataType === AttributeDataTypes.DATE ? US_DATE_FORMAT : TIMESTAMP_DATE_TIME_FORMAT) : '';

    if (
      (min && selected.isBefore(min))
      || (max && selected.isAfter(max))
    ) {
      const rangeDetail = constraints.rollingDays ? `(${constraints.rollingDays} days) ` : '';
      const errorMessage = `Date should be within the range ${rangeDetail}set in dataset materialization (${formattedMin} to ${formattedMax})`;
      setErrors(prev => ({
        ...prev,
        [name]: errorMessage,
      }));
    }
    else {
      setErrors(prev => {
        const updated = { ...prev };
        delete updated[name];
        return updated;
      });
    }

    // Build the new parameters with the updated value
    const newParameters = {
      ...runDetails.parameters,
      [name]: formattedValue,
    };

    // Validate start/end date range
    const startMatch = name.match(/^(.*)_start$/i);
    const endMatch = name.match(/^(.*)_end$/i);

    if (startMatch || endMatch) {
      const prefix = startMatch ? startMatch[1] : endMatch[1];
      const startKey = `${prefix}_start`;
      const endKey = `${prefix}_end`;
      // Use separate error key to avoid overwriting min/max constraint errors
      const dateRangeErrorKey = `${prefix}_date_range`;

      const startValue = newParameters[startKey];
      const endValue = newParameters[endKey];

      if (startValue && endValue) {
        const startDate = new Date(startValue);
        const endDate = new Date(endValue);

        if (startDate >= endDate) {
          setErrors(prev => ({
            ...prev,
            [dateRangeErrorKey]: `${startKey}: Start date must be before end date`,
          }));
        }
        else {
          setErrors(prev => {
            const updated = { ...prev };
            delete updated[dateRangeErrorKey];
            return updated;
          });
        }
      }
      else {
        // Clear date range error if one of the fields is empty
        setErrors(prev => {
          const updated = { ...prev };
          delete updated[dateRangeErrorKey];
          return updated;
        });
      }
    }

    updateRunDetails({
      ...runDetails,
      parameters: newParameters,
    });
  };

  const handleTextFieldChange = (e) => {
    updateRunDetails({
      ...runDetails,
      parameters: {
        ...runDetails.parameters,
        [name]: e.target.value,
      },
    });
    const input = e.target.value;
    const inputOptions = input.split(',').map(opt => opt.trim()).filter(Boolean);

    const trimmedInput = input.trim();
    const uniqueOptions = Array.from(new Set(inputOptions));
    const allowedValues = constraints.allowedValues || [];
    const hasNoDuplicates = uniqueOptions.length === inputOptions.length;
    const hasAllowedValues = allowedValues.length > 0 && inputOptions.length > 0;
    const allValuesAllowed = uniqueOptions.every(opt => allowedValues.includes(opt));
    const noTrailingComma = !trimmedInput.endsWith(',');
    const noLeadingComma = !trimmedInput.startsWith(',');
    const noDoubleComma = !input.includes(',,');
    const isValid = (allowedValues.length === 1 && allowedValues[0] === '')
      || (hasNoDuplicates
        && hasAllowedValues
        && allValuesAllowed
        && noTrailingComma
        && noLeadingComma
        && noDoubleComma);

    if (!isValid) {
      setErrors(prev => ({
        ...prev,
        [name]: `Only values set in dataset materialization are allowed and no duplicates allowed. Allowed values: ${constraints.allowedValues?.join(', ') || 'None'}`,
      }));
    }
    else {
      setErrors(prev => {
        const updated = { ...prev };
        delete updated[name];
        return updated;
      });
    }
  };

  if (dataType === AttributeDataTypes.TIMESTAMP || dataType === AttributeDataTypes.DATE) {
    const PickerComponent = dataType === AttributeDataTypes.TIMESTAMP ? DateTimePicker : DatePicker;

    // For TIMESTAMP, include seconds in the picker views
    const pickerViews = dataType === AttributeDataTypes.TIMESTAMP
      ? ['year', 'month', 'day', 'hours', 'minutes', 'seconds']
      : undefined;

    // For TIMESTAMP, show every second (not 5-second intervals)
    const timeSteps = dataType === AttributeDataTypes.TIMESTAMP
      ? { hours: 1, minutes: 1, seconds: 1 }
      : undefined;

    return (
      <PickerComponent
        sx={{
          width: '100%',
          '& > .MuiInputBase-root': {
            width: '100%',
          },
        }}
        label={name}
        format={fieldType}
        onChange={handlePickerChange}
        views={pickerViews}
        timeSteps={timeSteps}
        slotProps={{
          textField: {
            required: true,
            margin: 'dense',
            helperText: description,
            placeholder: exampleValue,
          },
        }}
        value={
          runDetails.parameters[name] ? (
            moment.utc(runDetails.parameters[name])
          ) : null
        }
        minDate={constraints.minDate}
        maxDate={constraints.maxDate}
      />
    );
  }

  return (
    <TextField
      fullWidth
      label={name}
      helperText={description || name}
      margin='dense'
      onChange={handleTextFieldChange}
      required
      type={fieldType}
      value={runDetails.parameters[name] || ''}
      variant='outlined'
      placeholder={exampleValue}
      inputProps={{
        step: 1,
      }}
    />
  );
};
